import { computed } from "vue";
import { GraphQLError } from "graphql/error/GraphQLError";
import { CtxLoadError, ServiceNotFoundError, SubscriptionError, useServiceStore } from "@/store/service";

// Reusable UI Error types for the app
export const UiErrorTypes = {
  SERVICE_NOT_FOUND: "service_not_found",
  PERMISSION_DENIED: "permission_denied",
  GRAPHQL: "graphql_error",
  CTX_LOAD: "ctx_load_error",
  SUBSCRIPTION: "subscription_error",
  NETWORK: "network_error",
  UNKNOWN: "unknown_error",
} as const;

export type UiErrorType = (typeof UiErrorTypes)[keyof typeof UiErrorTypes];

const denied = "PERMISSION_DENIED";

/**
 * Checks whether an Apollo error carries a GraphQL error with the given
 * extensions code (e.g. "PAYMENT_SETUP_REQUIRED"). Extensions codes are the
 * only backend error signal visible to non-admin users, since raw GraphQL
 * messages are surfaced to admins only (see useApolloErrorHandling).
 */
export function hasGraphQLErrorCode(error: unknown, code: string): boolean {
  return graphQLErrorCodes(error).includes(code);
}

/**
 * Collects the extensions codes carried by a GraphQL failure, from EITHER shape:
 *  - a thrown ApolloError (mutations): the errors live on `.graphQLErrors`;
 *  - a query response's `errors` array (errorPolicy "all" puts GraphQLErrors there
 *    instead of throwing) passed in directly.
 * Returns the codes in order; entries without a string `extensions.code` are dropped.
 */
export function graphQLErrorCodes(reason: unknown): string[] {
  const errors =
    (reason as { graphQLErrors?: readonly Pick<GraphQLError, "extensions">[] })?.graphQLErrors ??
    (Array.isArray(reason) ? (reason as readonly Pick<GraphQLError, "extensions">[]) : undefined);
  if (!errors) return [];
  return errors
    .map((graphQLError) => graphQLError.extensions?.code)
    .filter((code): code is string => typeof code === "string");
}

function classifyGraphQlErrors(errors: readonly GraphQLError[]): UiErrorType {
  if (errors.some((e) => e.extensions?.code === denied)) return UiErrorTypes.PERMISSION_DENIED;

  if (
    errors.some(
      (e) => e.message.includes("Failed to fetch") || e.message.includes("ECONN") || e.message.includes("timeout"),
    )
  )
    return UiErrorTypes.NETWORK;

  return UiErrorTypes.GRAPHQL;
}

// Derived state about the current error context
export const uiErrorType = computed<UiErrorType | undefined>(() => {
  const service = useServiceStore();

  const err = service.ctxLoadError as CtxLoadError | undefined;
  if (!err) return;

  if (err instanceof ServiceNotFoundError) return UiErrorTypes.SERVICE_NOT_FOUND;
  if (err instanceof SubscriptionError) return UiErrorTypes.SUBSCRIPTION;
  if (err instanceof CtxLoadError) {
    if (!err.errors?.length) return UiErrorTypes.CTX_LOAD;
    return classifyGraphQlErrors(err.errors);
  }

  return UiErrorTypes.UNKNOWN;
});

/** I18n key for the current error message */
export const uiErrorI18nKeyPath = computed(() => {
  switch (uiErrorType.value) {
    case UiErrorTypes.SERVICE_NOT_FOUND:
      return "ngp/load_error/not_found/text";
    case UiErrorTypes.PERMISSION_DENIED:
      return "ngp/load_error/error/not_owner";
    case UiErrorTypes.SUBSCRIPTION:
      return "ngp/load_error/error/subscription";
    case UiErrorTypes.CTX_LOAD:
      return "ngp/load_error/error/ctx_load";
    case UiErrorTypes.NETWORK:
      return "ngp/load_error/error/network";
    case UiErrorTypes.GRAPHQL:
      return "ngp/load_error/error/graphql";
    default:
      return "ngp/load_error/error/generic";
  }
});
